import { action, computed, observable, makeObservable } from 'mobx';
import {IServiceFactory} from '../../../service-factory.interface';
import {IGamesListViewModel} from "../lists/games-list-view-model.interface";
import {ISwipeLogicHandler} from "./swiper-logic-handler.interface";
import { ResizeObserver } from '@juggle/resize-observer';

export class SwipeLogicHandler implements ISwipeLogicHandler {

  private _resizeObserver: ResizeObserver | null = null;
  constructor(private services: IServiceFactory,
              private readonly _gamesListViewModel: IGamesListViewModel) {
    makeObservable(this);

    this._isRTLLanguage = this.services.language.isRTLLanguage;
  }

  @observable.ref
  private _element: HTMLElement = null;

  @observable.ref
  private _currentTranslateAmount: number = 0;

  @action.bound
  setElement(element: HTMLElement | null) {
    this._unsubscribe();
    if(element) {
      this._element = element;
      this._setScrollWidth(element.scrollWidth);
      this._currentTranslateAmount = this.services.window.getElementHorizontalTranslationAmount(this._element as Element);
      this._subscribe();
    } else {
      this.resetScrollPosition();
      this._element = null;
    }
  }

  @action.bound
  resetScrollPosition() {
    this._currentTranslateAmount = 0;
    if (this._element) {
      this._element['style'].transform = `translate3d(0px, 0, 0)`;
    }
  }

  private _subscribe() {

    if(!this._resizeObserver) {
      this._resizeObserver = new ResizeObserver(() => {
        this._setScrollWidth(this._element.scrollWidth);
      })
    }

    this._resizeObserver.observe(this._element);

    if(this.services.device.isMobile && this._element) {
      this._element.addEventListener("scroll", this._onMobileSwipe);
    }
  }

  private _unsubscribe() {

    if(this._resizeObserver) {
      this._resizeObserver.disconnect();
    }

    if(this.services.device.isMobile && this._element) {
      this._element.removeEventListener("scroll", this._onMobileSwipe);
    }
  }

  private readonly _isRTLLanguage : boolean;


  get allowPrevious(): boolean {
    if (this._element) {
      return Math.abs(this._currentTranslateAmount) >= 1;
    }

    return false;
  }

  get allowNext(): boolean {
    if (this._element) {
      return Math.abs(this._currentTranslateAmount) < this._maximumTranslationAmount - 1;
    }

    return false;
  }


  private get _width(): number {
    return this.services.window.getElementWidth(this._element);
  }


  @action.bound
  private _setScrollWidth(value: number): void {
    this._scrollWidth = value;
  }

  @observable.ref
  private _scrollWidth = 0;


  @computed
  private get _maximumTranslationAmount(): number {
    if(this._element) {
      return this._scrollWidth - this._width;
    }

    return 0;
  }

  private _isSwiperOnTheMove = false;

  @action.bound
  private _slide(computeIncrement: (absoluteCurrentTranslationAmount: number) => number) {
    if(this._isSwiperOnTheMove) {
      return;
    }

    this._isSwiperOnTheMove = true;

    try {
      let currentTranslateAmount = this.services.window.getElementHorizontalTranslationAmount(this._element);
      let increment = computeIncrement(Math.abs(currentTranslateAmount));

      if(this._isRTLLanguage) {
        increment = -1 * increment;
      }

      let translateAmount = currentTranslateAmount + increment;

      this._element['style'].transform = `translate3d(${translateAmount}px, 0, 0)`;
      this._currentTranslateAmount = translateAmount;

      this._sendAnalytics();
    } finally {
      this._isSwiperOnTheMove = false;
    }
  }

  private _sendAnalytics() {
    this.services.analytics.sendElasticInfoEvent({eventType:"CGP_Game_Click",category: this._gamesListViewModel.arenaName, action: 'Swipe', label: this._gamesListViewModel.title.translationKey}, true);
  }


  moveNext(): void {
    if(this.allowNext) {
      this._slide((absoluteCurrentTranslationAmount) => -1 * Math.min(this._width, this._maximumTranslationAmount - absoluteCurrentTranslationAmount));
    }

  }

  movePrevious(): void {
    if(this.allowPrevious) {
      this._slide((absoluteCurrentTranslationAmount) => Math.min(this._width, absoluteCurrentTranslationAmount));
    }
  }

  private _mobileScrollingTimeout: any;
  private _onMobileSwipe = () => {
    //Clear the timeout on every scroll to prevent form the scroll(swipe) event to fire multiple times. (debounce)
    clearTimeout(this._mobileScrollingTimeout);
    // Set a timeout to fire after the scrolling as end
    this._mobileScrollingTimeout = setTimeout(() => {
      this._sendAnalytics();
    }, 100);
  };
}